// Apolo IDE — SessionRulesTab side-panel (Sprint self-improving E2 / V2).
//
// Shows the ephemeral rules learned in THIS session — the corrections E9's
// auto-test loop turned into one-line rules, injected into the system prompt
// for the rest of the session. Read + dismiss. They vanish when the session
// closes (server-side expiry). Distinct from RulesTab (the .apolorules editor).

import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Loader2, Sparkles, Wrench, X } from 'lucide-react';
import { apiUrl } from '../../../lib/apiBase.js';
import { getStoredToken } from '../../../lib/api.js';

async function fetchJSON(path, init = {}) {
  const token = getStoredToken();
  const res = await fetch(apiUrl(path), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  let body = null;
  try { body = await res.json(); } catch { body = null; }
  return { ok: res.ok, status: res.status, body };
}

export default function SessionRulesTab({ sessionId }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    const res = await fetchJSON(`/api/code-ide/sessions/${sessionId}/session-rules`);
    if (!res.ok) {
      setError(res.body?.error || `Falha ao carregar (HTTP ${res.status})`);
      setLoading(false);
      return;
    }
    setRules(Array.isArray(res.body?.rules) ? res.body.rules : []);
    setLoading(false);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setRules([]);
      setError(null);
      return undefined;
    }
    load();
    return undefined;
  }, [sessionId, load]);

  const dismiss = useCallback(async (ruleId) => {
    // Optimistic removal; reload on failure to resync.
    setRules((prev) => prev.filter((r) => r.id !== ruleId));
    const res = await fetchJSON(`/api/code-ide/sessions/${sessionId}/session-rules/${ruleId}`, { method: 'DELETE' });
    if (!res.ok) load();
  }, [sessionId, load]);

  if (!sessionId) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-xs text-apolo-textTertiary">
        Abra uma sessão para ver as regras aprendidas nela.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <div className="text-xs text-apolo-textTertiary">
        Correções aplicadas nesta sessão viram regras de alta prioridade no system prompt
        até a sessão fechar. Dispense as que não quiser manter.
      </div>

      {error && (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-apolo-error/30 bg-apolo-errorBg p-2 text-xs text-apolo-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-[11px] text-apolo-textTertiary">
          <Loader2 size={12} className="animate-spin" aria-hidden="true" /> Carregando…
        </div>
      )}

      {!loading && !error && rules.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-xs text-apolo-textTertiary">
          <Sparkles size={18} className="text-apolo-textTertiary" aria-hidden="true" />
          <span>Nenhuma regra aprendida nesta sessão ainda.</span>
        </div>
      )}

      <ul className="flex flex-col gap-2">
        {rules.map((rule) => (
          <li
            key={rule.id}
            className="flex items-start gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-2 text-xs text-apolo-textPrimary"
          >
            <span
              className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded bg-apolo-surface3 px-1.5 py-0.5 text-[10px] text-apolo-textSecondary"
              title={rule.source === 'e9_fix' ? 'Derivada de um fix automático (E9)' : 'Adicionada manualmente'}
            >
              {rule.source === 'e9_fix'
                ? <><Wrench size={10} aria-hidden="true" /> auto</>
                : <>manual</>}
            </span>
            <span className="flex-1 leading-relaxed">{rule.rule}</span>
            <button
              type="button"
              onClick={() => dismiss(rule.id)}
              aria-label="Dispensar regra"
              title="Dispensar regra"
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-apolo-borderSubtle text-apolo-textTertiary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary"
            >
              <X size={12} aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
